import React, { useMemo, useRef, useState } from "react";
import { Box, Popover, Typography } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import { B2BTextField, MobilePopoverCard, ActionLink, CloseButtonIcon, AirlineIcon } from "halifax";
import clsx from "clsx";
import { IataAirline } from "../iataAirlines";
import {
  AIRLINE_LABEL,
  AIRLINE_PLACEHOLDER,
  AIRLINE_SEARCH_PLACEHOLDER,
  AIRLINE_NO_RESULTS,
  AIRLINE_TRUNCATED_HINT,
} from "../constants";
import "./styles.scss";

// Cap how many rows render at once. The full list is ~960 carriers; mounting
// them all on open janks the dropdown, so we render a slice and rely on the
// search box to narrow. 100 rows renders smoothly.
const MAX_RENDERED_AIRLINES = 100;

export interface IAirlineSelectProps {
  airlines: IataAirline[];
  /** Selected 2-char IATA code, if any. */
  value?: string;
  onSelect: (code: string) => void;
  isMobile?: boolean;
  disabled?: boolean;
  id?: string;
  /** Codes to feature (in order) as the default list before the user searches. */
  popularCodes?: string[];
}

export const AirlineSelect = ({
  airlines,
  value,
  onSelect,
  isMobile,
  disabled,
  id = "airline-select",
  popularCodes,
}: IAirlineSelectProps) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [anchorWidth, setAnchorWidth] = useState<number | undefined>(undefined);

  const selectedAirline = useMemo(
    () => airlines.find((a) => a.code === value),
    [airlines, value]
  );

  const sortedAirlines = useMemo(
    () => [...airlines].sort((a, b) => a.name.localeCompare(b.name)),
    [airlines]
  );

  // Featured airlines (in popularity order) shown before the user searches.
  // Codes missing from the list are skipped so a stale code can't break render.
  const popularAirlines = useMemo(() => {
    if (!popularCodes?.length) return null;
    const byCode = new Map(airlines.map((a) => [a.code, a]));
    return popularCodes
      .map((code) => byCode.get(code))
      .filter((a): a is IataAirline => a != null);
  }, [airlines, popularCodes]);

  const matchedAirlines = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return popularAirlines ?? sortedAirlines;
    return sortedAirlines.filter(
      (a) =>
        a.name.toLowerCase().includes(query) ||
        a.code.toLowerCase().includes(query)
    );
  }, [sortedAirlines, popularAirlines, search]);

  const visibleAirlines = matchedAirlines.slice(0, MAX_RENDERED_AIRLINES);
  const isTruncated = matchedAirlines.length > visibleAirlines.length;

  const openPanel = () => {
    if (disabled) return;
    setAnchorWidth(anchorRef.current?.offsetWidth);
    setOpen(true);
  };

  const closePanel = () => {
    setOpen(false);
    setSearch("");
  };

  const handleSelect = (code: string) => {
    onSelect(code);
    closePanel();
  };

  const list = (
    <ul className="airline-select-list" role="listbox" aria-label={AIRLINE_LABEL}>
      {matchedAirlines.length === 0 && (
        <li className="airline-select-empty">
          <Typography variant="body2">{AIRLINE_NO_RESULTS}</Typography>
        </li>
      )}
      {visibleAirlines.map((airline) => (
        <li key={airline.code}>
          <button
            type="button"
            role="option"
            aria-selected={airline.code === value}
            className={clsx("airline-select-option", {
              selected: airline.code === value,
            })}
            onClick={() => handleSelect(airline.code)}
          >
            <AirlineIcon
              airlineCode={airline.code}
              size="3x"
              altText=""
              className="airline-select-logo"
            />
            <Box className="airline-select-option-text">
              <Typography variant="body1" className="airline-name">
                {airline.name}
              </Typography>
              <Typography variant="body2" className="airline-code">
                {airline.code}
              </Typography>
            </Box>
          </button>
        </li>
      ))}
      {isTruncated && (
        <li className="airline-select-truncated">
          <Typography variant="caption">{AIRLINE_TRUNCATED_HINT}</Typography>
        </li>
      )}
    </ul>
  );

  const searchField = (
    <B2BTextField
      className="airline-select-search"
      fullWidth
      value={search}
      onChange={(val: string) => setSearch(val)}
      placeholder={AIRLINE_SEARCH_PLACEHOLDER}
      type="text"
      id={`${id}-search`}
      autoFocus
    />
  );

  return (
    <Box className={clsx("airline-select", { mobile: isMobile })}>
      <button
        type="button"
        ref={anchorRef}
        id={id}
        className={clsx("airline-select-field", { open, disabled })}
        onClick={openPanel}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Box className="airline-select-field-content">
          <Typography variant="caption" className="airline-select-field-label">
            {AIRLINE_LABEL}
          </Typography>
          <Typography
            variant="body1"
            className={clsx("airline-select-field-value", {
              placeholder: !selectedAirline,
            })}
          >
            {selectedAirline ? selectedAirline.name : AIRLINE_PLACEHOLDER}
          </Typography>
        </Box>
        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </button>

      {isMobile
        ? open && (
            <MobilePopoverCard
              open={open}
              onClose={closePanel}
              className="airline-select-mobile-popover"
              contentClassName="airline-select-mobile-content"
              headerElement={
                <Box className="airline-select-mobile-header">
                  <Typography className="header-title">
                    {AIRLINE_LABEL}
                  </Typography>
                  <ActionLink
                    className="modal-close-button"
                    content={<CloseButtonIcon />}
                    label="Close"
                    onClick={closePanel}
                  />
                </Box>
              }
            >
              {searchField}
              {list}
            </MobilePopoverCard>
          )
        : (
          <Popover
            open={open}
            anchorEl={anchorRef.current}
            onClose={closePanel}
            classes={{ paper: "airline-select-popover-paper" }}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            getContentAnchorEl={null}
            PaperProps={{ style: anchorWidth ? { width: anchorWidth } : undefined }}
          >
            {searchField}
            {list}
          </Popover>
        )}
    </Box>
  );
};
